import { Inject, Injectable } from '@angular/core';

import { APP_BASE_HREF } from '@angular/common';

import { Am5ChartConfiguration } from '../models/am5-chart-configuration.model';
import { Am5ChartSettings } from '../models/am5-chart-settings.model';
import { chartColors } from '../enums/chart-colors.enum';
import { ChartMetricsEnum, chartMetricsToDateAndValueXYChartUnitMap } from '../../../Common/Reports/enums/chart-metrics.enum';
import { ChartSegmentModel } from '../../../Common/Reports/models/chart-segment.model';
import { ChartSeries } from '../models/chart-series.model';
import { ChartSpecificTypesEnum } from '../../../Common/Reports/enums/chart-specific-types.enum';
import { defaultSeriesColorConfig } from '../config/default-series-color.config';
import { DateAndValueXYChartUnitEnum } from '../enums/date-and-value-x-y-chart-unit.enum';
import { LabelDisplayOptionsEnum } from '../../../Common/Misc/enums/label-display-options.enum';
import { LocalizationService } from '../../../Core/Localization/services/localization.service';
import { ReportBlockTypeEnum } from '../../../Common/Reports/enums/report-block-type.enum';
import { ChartAnnotationIndicatorTypesEnum } from '../../../Common/Dialogs/components/ChartAnnotationDialog/chart-annotation-marker-types.enum';
import { Am5Chart } from '../models/am5-chart.model';
import { Am5CategoryValueXYBaseChartConfiguration } from '../models/Am5CategoryValueXYChart/am5-category-value-x-y-base-chart-configuration.model';


@Injectable()
export class Am5ChartTooltipService {

    series: ChartSeries[];
    config: Am5ChartConfiguration;
    settings: Am5ChartSettings;
    chartSeriesIndex: number;
    isPreview: boolean;
    metric: ChartMetricsEnum;
    doLink: boolean = true;
    customTooltipLink: string;

    constructor(
        public localizationService: LocalizationService,
        @Inject(APP_BASE_HREF) public appBaseHref: string,
    ) {}

    public getChartTooltip(
        series: ChartSeries[],
        config: Am5ChartConfiguration,
        settings: Am5ChartSettings,
        chartSeriesIndex: number,
        isPreview: boolean
    ): string {
        this.series = series;
        this.config = config;
        this.settings = settings;
        this.chartSeriesIndex = chartSeriesIndex;
        this.isPreview = isPreview;
        switch (this.config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                this.metric = this.series[0].key;
                return this.getDateValueXYChartTooltip();
            case ReportBlockTypeEnum.SENTIMENT_TREND:
                return this.getStackedSharedTooltip();
            case ReportBlockTypeEnum.TOP_10_AUTHORS_BY_VOLUME:
            case ReportBlockTypeEnum.TOP_10_OUTLETS:
            case ReportBlockTypeEnum.TOP_HASHTAGS:
            case ReportBlockTypeEnum.TOP_SOCIAL_REFERRERS:
                return this.getCategoryValueXYChartTooltip();
        }

    }

    getDateValueXYChartTooltip(): string {
        const segment = {} as ChartSegmentModel;
        const icon = this.settings.chartSpecificType === ChartSpecificTypesEnum.LINE ?
            'fa-solid fa-code-commit' : 'fa-solid fa-square';

        return this.getSegmentTooltipHTML(
            segment,
            null,
            'valueY',
            `{valueLabel${this.chartSeriesIndex}}` as string,
            icon,
            this.doLink
        );
    }

    public getStackedSharedTooltip(): string {
        const rows = this.series.map((series, index) => {
            const color = this.settings.colorMap?.[series.colorMapKey]
                || this.settings.colorMap?.[series.key]
                || defaultSeriesColorConfig[index % defaultSeriesColorConfig.length];

            return `
                <div style="margin-top: 10px; display: flex; align-items: center; justify-content: flex-start;">
                    <span style="flex-shrink: 0; width: 12px; height: 12px; background: ${color}; margin-right: 10px;"></span>
                    <p style="margin-right: 10px; word-break: break-word;">
                        <b style="font-weight: 700;"> {valueLabel${index}}:</b>
                        {value${index}} ({percent${index}.formatNumber('#.#')}%)
                    </p>
                    ${this.doLink ? this.getSharedTooltipLink(index) : ''}
                </div>`;
        }).reverse().join('');

        return `
            <div style="font-size: 0.875rem; width: ${this.doLink ? '320' : '250'}px; color: ${chartColors.agprBlack}">
                <div style="border-bottom: 1px solid #ced4da; padding-bottom: 5px">
                    <b style="font-weight: 700">{label}</b>
                </div>
                ${rows}
            </div>
        `;
    }

    private getSharedTooltipLink(seriesIndex: number): string {
        if (this.isPreview) {
            return `<div style="margin-left: auto; min-width: 100px; align-self: flex-end;">
                        <span style="color: #5E6473; font-size: 0.875rem; cursor: not-allowed;">
                        ${this.getLinkLabel()}
                        </span>
                    </div>`;
        }
        return `<div>
                    <a
                        class="viewMentionLink"
                        data-series-index="${seriesIndex}"
                        href="{viewUrl${seriesIndex}}"
                        style="color: inherit;
                            text-decoration: underline;
                            font-size: 0.875rem;
                            cursor: pointer
                        "
                    >
                    ${this.getLinkLabel()}
                    </a>
                </div>`;
    }

    getCategoryValueXYChartTooltip(): string {
        return this.getSegmentTooltipHTML(
            {} as ChartSegmentModel,
            'percent0',
            'value0',
            null,
            'fa-solid fa-square',
            this.doLink,
            false,
            false,
            '',
            '',
            '',
            (this.config as Am5CategoryValueXYBaseChartConfiguration).noPercentage
        );
    }

    /**
     * @param segment chart segment
     * @param percentKey key to get percent value
     * @param countKey key to get count value,
     * @param valueLabel label for the value (eg: series name, sentiment type)
     * @param tooltipIcon: tooltip icon
     * @param doLink determine if tooltip should have view mention link
     * @param hasPreviousPeriod used in a tooltip that's displayed for multiple periods at the same time
     * @param isPreviousPeriod used in a tooltip that's displayed for a single previous period segment
     * @returns the html for the tooltip when hover over the chart data
     */
    private getSegmentTooltipHTML(
        segment: ChartSegmentModel,
        percentKey?: string,
        countKey?: string,
        valueLabel?: string,
        tooltipIcon?: string,
        doLink = true,
        hasPreviousPeriod?: boolean,
        isPreviousPeriod?: boolean,
        previousPeriodCountKey: string = 'xValuePreviousPeriod',
        previousPeriodPercentKey: string = percentKey,
        previousPeriodIcon?: string,
        noPercentage: boolean = false
    ): string {
        if (!previousPeriodIcon && tooltipIcon) {
            previousPeriodIcon = tooltipIcon;
        }
        const previousPeriod =  this.localizationService ? this.localizationService.transform('Reports.series.previousPeriod') : 'Previous Period';
        const currentPeriod =  this.localizationService ? this.localizationService.transform('Reports.series.currentPeriod') : 'Current Period';
        const html = `
            <div
                style="font-size: 0.875rem; width: 100%; height: 50px; color: ${chartColors.agprBlack}"
            >
                <div style="border-bottom: 1px solid #ced4da; padding-bottom: 5px">
                    <b style="font-weight: 700">
                        {label}${isPreviousPeriod ? ` (${previousPeriod})` : ''}
                    </b>
                </div>
                <div style="margin-top: 10px; display: flex; align-items: center; justify-content: flex-start;">
                    ${tooltipIcon ? this.getTooltipIcon(segment, tooltipIcon, 'color') : ''}
                    <p style="margin-right: 10px; word-break: break-word;">
                        ${valueLabel ? `<b style="font-weight: 700;"> ${valueLabel}:</b>` : ''}
                        ${hasPreviousPeriod ? `<b style="font-weight: 700;"> ${currentPeriod}:</b>` : ''}
                        ${this.getCountAndPercentLabel(segment, percentKey, countKey, noPercentage)}
                    </p>
                    ${doLink ? this.getTooltipLink() : ''}
                </div>
                ${hasPreviousPeriod ? `
                    <div style="margin-top: 10px; display: flex; align-items: center">
                    ${previousPeriodIcon ? this.getTooltipIcon(segment, previousPeriodIcon, 'color') : ''}
                    <p style="margin-right: 10px; word-break: break-word;">
                        <b style="font-weight: 700;"> ${previousPeriod}:</b>
                        ${this.getCountAndPercentLabel(segment, previousPeriodPercentKey, previousPeriodCountKey, noPercentage)}
                    </p>
                    ${doLink ? this.getTooltipLink() : ''}
                    </div>` : ''}
            </div>
        `;

        return html;
    }

    private getCountAndPercentLabel(
        segment: ChartSegmentModel,
        percentKey = 'value.percent',
        countKey = 'value',
        noPercentage: boolean = false
    ): string {
        let valueLabel = '';
        if (this.settings.enableDataDisplaySettings) {
            if (this.settings.labelDisplayOptions) {
                if (noPercentage) {
                    valueLabel += `{${countKey}}`;
                } else {
                    valueLabel += `{${percentKey}.formatNumber(\'#.#\')}% ({${countKey}})`;
                }
            }
        } else {
            valueLabel = `{${countKey}${this.getFormatNumberString(this.metric)}}`;
        }

        return valueLabel;
    }

    private getTooltipIcon(segment: ChartSegmentModel, icon: string, colorKey: string) {
        return `<i
                class="${icon}"
                style="display: flex; flex-shrink: 0; width: 14px; height: 14px; color: {color}; margin-right: 10px;"
            ></i>`;
    }

    private getTooltipLink() {
        // we want to disable the tooltip in preview
        if (this.isPreview) {
            return `<div style="
                        margin-left: auto;
                        min-width: 100px;
                        align-self: flex-end;
                        ">
                        <span
                            style="color: #5E6473;
                            font-size: 0.875rem;
                            cursor: not-allowed;
                        "
                        >
                        ${this.getLinkLabel()}
                        </span>
                    </div>`;
        } else {
            return `<div>
                    <a
                        class="viewMentionLink"
                        href=""
                        style="color: inherit;
                            text-decoration: underline;
                            font-size: 0.875rem;
                            cursor: pointer
                        "
                    >
                    ${this.getLinkLabel()}
                    </a>
                </div>`;
        }
    }

    private getLinkLabel(): string {
        if (this.customTooltipLink) {
            return this.localizationService.transform(this.customTooltipLink);
        }
        return this.localizationService.transform('Reports.chart.segmentCoverageAnalysisLabel');
    }

    public getIndicatorTooltip(indicator: any, chartSeriesIndex: number, chart: Am5Chart, sourceChartSeries: ChartSeries): string {
        let content;
        let label = '';
        switch (chart.config.type) {
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME:
                label = this.localizationService.transform(`Reports.formField.analysis.${indicator.metric}`);
                break;
            case ReportBlockTypeEnum.COVERAGE_OVER_TIME_COMPARISON:
                label = sourceChartSeries.label;
                break;
            case ReportBlockTypeEnum.COVERAGE_IMPACT_TREND:
                label = indicator.metric === ChartMetricsEnum.volumeOfMentions
                    ? this.localizationService.transform(`Reports.formField.analysis.${indicator.metric}`)
                    : this.localizationService.transform(`Reports.googleAnalyticsCharts.${indicator.metric}`);
                break;
        }
        const valueString = `<span>{value${chartSeriesIndex}${this.getFormatNumberString(indicator.metric)}}</span>`;
        let currencyString = '';
        if (indicator.metric === ChartMetricsEnum.ave) {
            currencyString = `<span style="display: inline-block; margin-left: 5px">${chart.settings.currencyCode}</span>`;
        }
        switch (indicator.indicatorType) {
            case ChartAnnotationIndicatorTypesEnum.AVERAGE:
                content = `<div><div style="font-weight: 700; display: inline-block; margin-right: 10px">`
                    + this.localizationService.transform(`Reports.annotation.${ChartAnnotationIndicatorTypesEnum.AVERAGE}`) + ' '
                    + label + ': </div>'+ valueString + currencyString + '</div>';
                break;
            case ChartAnnotationIndicatorTypesEnum.TREND:
                content = '<div>' + this.localizationService.transform(
                    `Reports.annotation.${indicator.indicatorType}.tooltip`,
                    {
                        label: label
                    }
                ) + '</div>';
                break;
        }


        return `<div style="height: 16px; color: ${chartColors.agprText}">${content}</div>`;
    }

    private getFormatNumberString(metric: ChartMetricsEnum): string {
        const unit = chartMetricsToDateAndValueXYChartUnitMap.get(metric);
        let format = '';
        switch (unit) {
            case DateAndValueXYChartUnitEnum.NUMBER:
                format = '.formatNumber(\'#,###.#a\')';
                break;
            case DateAndValueXYChartUnitEnum.PERCENTAGE:
                format = '.formatNumber(\'#.#\')}%';
                break;
            case DateAndValueXYChartUnitEnum.CURRENCY:
                format = '.formatNumber(\'#,###.00a\')';
                break;
        }

        return format;
    }

    public setDoLink(doLink: boolean): void {
        this.doLink = doLink;
    }

    public setCustomTooltipLink(customTooltipLink: string): void {
        this.customTooltipLink = customTooltipLink;
    }
}
